const EXCEL_CELL_SAFE_LENGTH = 30000;
const DEFAULT_CONFIDENCE_THRESHOLD = 0.7;

const hasValue = (value) => (
  value !== undefined
  && value !== null
  && value !== ''
);

export const normalizeRecognitionWarnings = (warnings) => {
  if (!Array.isArray(warnings)) return [];
  return warnings
    .map(warning => String(warning || '').trim())
    .filter(Boolean);
};

export const normalizeRecognitionConfidence = (value) => {
  if (!hasValue(value)) return null;

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value > 1 ? Math.min(value / 100, 1) : Math.max(value, 0);
  }

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return null;
  if (['high', '높음', 'good'].includes(normalized)) return 1;
  if (['medium', '중간', '확인 필요'].includes(normalized)) return 0.65;
  if (['low', '낮음', '수정 권장'].includes(normalized)) return 0.35;

  const numeric = Number(normalized.replace('%', ''));
  if (!Number.isFinite(numeric)) return null;
  return numeric > 1 ? Math.min(numeric / 100, 1) : Math.max(numeric, 0);
};

export const formatRecognitionConfidence = (value) => {
  const normalized = normalizeRecognitionConfidence(value);
  if (normalized === null) return '';
  return `${Math.round(normalized * 100)}%`;
};

export const getRecognitionReviewState = ({
  name,
  confidence,
  warnings = [],
  content = '',
  threshold = DEFAULT_CONFIDENCE_THRESHOLD,
} = {}) => {
  const normalizedWarnings = normalizeRecognitionWarnings(warnings);
  const normalizedConfidence = normalizeRecognitionConfidence(confidence);
  const reasons = [];

  if (name !== undefined && !String(name || '').trim()) {
    reasons.push('학생 이름이 비어 있어요');
  }
  if (!String(content || '').trim()) {
    reasons.push('인식된 내용이 비어 있어요');
  }
  if (normalizedConfidence !== null && normalizedConfidence < threshold) {
    reasons.push('인식 신뢰도가 낮아요');
  }
  normalizedWarnings.forEach((warning) => {
    if (!reasons.includes(warning)) reasons.push(warning);
  });

  return {
    needsReview: reasons.length > 0,
    reasons,
    confidence: normalizedConfidence,
    warnings: normalizedWarnings,
  };
};

export const formatRecognitionSourcePages = (pages) => {
  if (Array.isArray(pages)) {
    const normalized = pages
      .map(page => String(page ?? '').trim())
      .filter(Boolean);
    return normalized.length > 0 ? `${normalized.join(', ')}쪽` : '';
  }

  const normalized = String(pages ?? '').trim();
  if (!normalized) return '';
  return /쪽$/.test(normalized) ? normalized : `${normalized}쪽`;
};

export const splitTextForExcel = (
  value,
  columnLabel,
  maxLength = EXCEL_CELL_SAFE_LENGTH,
) => {
  const text = String(value ?? '');
  const safeLength = Math.max(1, Number(maxLength) || EXCEL_CELL_SAFE_LENGTH);
  if (text.length <= safeLength) {
    return { [columnLabel]: text };
  }

  const columns = {};
  let offset = 0;
  let chunkIndex = 0;
  while (offset < text.length) {
    let endOffset = Math.min(offset + safeLength, text.length);
    const lastCodeUnit = text.charCodeAt(endOffset - 1);
    const nextCodeUnit = text.charCodeAt(endOffset);
    const splitsSurrogatePair = (
      endOffset < text.length
      && lastCodeUnit >= 0xD800
      && lastCodeUnit <= 0xDBFF
      && nextCodeUnit >= 0xDC00
      && nextCodeUnit <= 0xDFFF
    );
    if (splitsSurrogatePair) endOffset -= 1;
    if (endOffset <= offset) endOffset = Math.min(offset + safeLength + 1, text.length);
    const label = chunkIndex === 0 ? columnLabel : `${columnLabel} ${chunkIndex + 1}`;
    columns[label] = text.slice(offset, endOffset);
    offset = endOffset;
    chunkIndex += 1;
  }
  return columns;
};

export const buildRecognitionExportRows = (students = [], {
  contentColumnLabel = '인식 내용',
  getName = student => student?.name || student?.student_name || '',
  getContent = student => student?.existing_content || student?.content || student?.answer_text || '',
  getSourcePages = student => student?.source_pages || student?.pages || '',
  getSourceFileName = student => student?.source_file_name || student?.fileName || student?.filename || '',
} = {}) => (
  (Array.isArray(students) ? students : []).map((student, index) => {
    const name = String(getName(student) || '');
    const content = getContent(student);

    return {
      '번호': index + 1,
      '학생명': name.trim() || `학생 ${index + 1}`,
      ...splitTextForExcel(content, contentColumnLabel),
      '원본 쪽': formatRecognitionSourcePages(getSourcePages(student)),
      '원본 파일명': String(getSourceFileName(student) || ''),
    };
  })
);

const getColumnWidth = (header) => {
  if (/인식 내용|답안 원문/.test(header)) return 90;
  if (/확인할 내용|인식 안내/.test(header)) return 50;
  if (/파일명/.test(header)) return 32;
  if (/학생명/.test(header)) return 14;
  if (/번호/.test(header)) return 7;
  return 15;
};

export const writeRecognitionWorkbook = (XLSX, rows, {
  filename,
  sheetName = '인식 결과',
} = {}) => {
  if (!XLSX?.utils || typeof XLSX.writeFile !== 'function') {
    throw new Error('엑셀 생성 도구를 불러오지 못했어요.');
  }
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new Error('다운로드할 인식 내용이 없어요.');
  }

  const headers = [];
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((header) => {
      if (!headers.includes(header)) headers.push(header);
    });
  });

  const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
  worksheet['!cols'] = headers.map(header => ({ wch: getColumnWidth(header) }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, String(sheetName || '인식 결과').slice(0, 31));
  XLSX.writeFile(workbook, filename);
};

export const getRecognitionExportDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};
